import React, { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Clock,
  Calendar,
  FileText,
  Users,
  ScanLine,
  LogIn,
  Download,
  ShieldCheck,
  CheckCircle,
  XCircle,
  Timer,
  RefreshCw,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import PillToggle from '../UI/PillToggle';
import useUsageAnalytics from '../../hooks/useUsageAnalytics';
import PeakUsagePeriods from './PeakUsagePeriods';
import { getLeaveTypeBgClass } from '../../utils/leaveTypeBadgeColors';

// Canonical leave-type identity (utils/leaveTypeBadgeColors.js) is defined at the
// -100/-50 badge shade; bump it to the -500 fill shade so the bar reads clearly
// against the gray-100 track while keeping each leave type's standard hue.
const leaveTypeBarClass = (name) =>
  getLeaveTypeBgClass(name).replace(/-(50|100|200)\b/, '-500');

// ── Icon colour palettes (mirrors the AdminDashboard KPI card scheme) ────────
const ICON_STYLES = {
  blue: { bg: 'bg-blue-100', fg: 'text-blue-800' },
  indigo: { bg: 'bg-indigo-100', fg: 'text-indigo-600' },
  emerald: { bg: 'bg-emerald-100', fg: 'text-emerald-600' },
  amber: { bg: 'bg-amber-100', fg: 'text-amber-600' },
  red: { bg: 'bg-red-100', fg: 'text-red-600' },
  purple: { bg: 'bg-purple-100', fg: 'text-purple-600' },
  teal: { bg: 'bg-teal-100', fg: 'text-teal-700' },
  gray: { bg: 'bg-gray-100', fg: 'text-gray-600' },
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString();
};

// Convert a decimal hour value into a compact "Xh Ym" string (e.g. 9.3 → "9h 18m").
const formatHoursMinutes = (value) => {
  const totalMinutes = Math.round(Number(value || 0) * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
};

// ── Reusable metric card (DS "Metrics Card" pattern) ─────────────────────────
const MetricCard = ({ icon: Icon, color = 'gray', value, label, sub }) => {
  const style = ICON_STYLES[color] || ICON_STYLES.gray;
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 flex flex-col">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 ${style.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${style.fg}`} aria-hidden="true" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
        </div>
      </div>
      {sub && (
        <div className="mt-auto pt-3">
          <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 max-w-full truncate">
            {sub}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Funnel stat tile ─────────────────────────────────────────────────────────
const FunnelStat = ({ icon: Icon, color = 'gray', value, label }) => {
  const style = ICON_STYLES[color] || ICON_STYLES.gray;
  return (
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <div className={`w-8 h-8 sm:w-9 sm:h-9 ${style.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${style.fg}`} aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-base sm:text-lg font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-xs text-gray-500 truncate">{label}</p>
      </div>
    </div>
  );
};

// ── Ranked breakdown bar list (leave types, document types, audit actions) ───
const BreakdownPanel = ({ icon: Icon, iconClass, title, totalLabel, items, barClass, barClassFor, maxCount }) => {
  if (!items?.length) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-5 h-5 flex-shrink-0 ${iconClass}`} />
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h3>
        {totalLabel && (
          <span className="ml-auto text-xs text-gray-500">{totalLabel}</span>
        )}
      </div>
      <div className="space-y-3">
        {items.slice(0, 8).map((item) => (
          <div key={item.type || item.action}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs sm:text-sm text-gray-700 truncate">{item.label}</span>
              <span className="text-xs sm:text-sm font-semibold text-gray-900 ml-2 flex-shrink-0">
                {formatNumber(item.count)}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barClassFor ? barClassFor(item) : barClass}`}
                style={{ width: `${maxCount > 0 ? (item.count / maxCount) * 100 : 0}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Chart series config per view mode ────────────────────────────────────────
// Clock-in/out colours match Attendance.js event dots (bg-green-500 / bg-red-500).
const CLOCK_IN_COLOR = '#22c55e';
const CLOCK_OUT_COLOR = '#ef4444';

const CHART_SERIES = {
  attendance: [
    { key: 'clock_in', name: 'Clock-Ins', color: CLOCK_IN_COLOR },
    { key: 'clock_out', name: 'Clock-Outs', color: CLOCK_OUT_COLOR },
  ],
  leave: [
    { key: 'leave_submitted', name: 'Submitted', color: '#d97706' },
    { key: 'leave_approved', name: 'Approved', color: '#16a34a' },
  ],
  documents: [
    { key: 'documents_uploaded', name: 'Uploaded', color: '#dc2626' },
    { key: 'ocr_processed', name: 'OCR Processed', color: '#9333ea' },
  ],
  logins: [
    { key: 'logins', name: 'Logins', color: '#0d9488' },
  ],
};

const RANGE_OPTIONS = [
  { id: '7', label: '7D', title: 'Last 7 days' },
  { id: '30', label: '30D', title: 'Last 30 days' },
  { id: '90', label: '90D', title: 'Last 90 days' },
  { id: 'all', label: 'All', title: 'Since go-live (1 Mar 2026)' },
];

const CHART_VIEW_OPTIONS = [
  { id: 'attendance', label: 'Attendance', title: 'Clock-in / clock-out volume' },
  { id: 'leave', label: 'Leave', title: 'Leave submissions vs approvals' },
  { id: 'documents', label: 'Documents', title: 'Document uploads vs OCR runs' },
  { id: 'logins', label: 'Logins', title: 'Daily login volume' },
];

// Weekday×hour rhythm needs a rolling window (backend max 180d). When the page
// is in lifetime / All mode, keep totals month-bucketed but still show rhythm
// from this fixed window so HR can pick deploy slots without switching away.
const RHYTHM_ROLLING_DAYS = 90;

const UsageAnalyticsSection = () => {
  const [range, setRange] = useState('30');
  const [chartView, setChartView] = useState('attendance');
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isFetching } = useUsageAnalytics(range);

  const generatedAt = data?.generated_at ? new Date(data.generated_at) : null;
  const freshnessLabel = isFetching
    ? 'Refreshing…'
    : generatedAt
    ? `Updated ${formatDistanceToNow(generatedAt, { addSuffix: true })}`
    : null;

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['usage'] });
  }, [queryClient]);

  const isAllTime = range === 'all';
  const granularity = data?.range?.granularity || 'day';

  const totals = data?.totals || {};
  const today = data?.today || {};
  const leaveFunnel = data?.leave_funnel || {};
  const docFunnel = data?.document_funnel || {};
  const engagement = data?.engagement || {};
  const leaveTypeBreakdown = data?.leave_type_breakdown || [];
  const documentTypeBreakdown = data?.document_type_breakdown || [];

  const chartData = useMemo(() => {
    if (!data?.timeseries) return [];
    const fmt = granularity === 'month' ? 'MMM yyyy' : 'd MMM';
    return data.timeseries.map((pt) => {
      let label = pt.date;
      try {
        label = format(parseISO(pt.date), fmt);
      } catch (e) {
        // keep ISO fallback
      }
      return { ...pt, label };
    });
  }, [data, granularity]);

  const maxLeaveType = useMemo(
    () => leaveTypeBreakdown.reduce((m, a) => Math.max(m, a.count || 0), 0),
    [leaveTypeBreakdown]
  );

  const maxDocumentType = useMemo(
    () => documentTypeBreakdown.reduce((m, a) => Math.max(m, a.count || 0), 0),
    [documentTypeBreakdown]
  );

  const hasActivity = useMemo(() => {
    const t = totals || {};
    return (
      (t.clock_in || 0) + (t.clock_out || 0) + (t.leave_submitted || 0) +
      (t.documents_uploaded || 0) + (t.audit_events || 0) > 0
    );
  }, [totals]);

  const activeSeries = CHART_SERIES[chartView] || CHART_SERIES.attendance;

  return (
    <section className="space-y-6" aria-label="Platform usage and adoption analytics">
      {/* Control bar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-gray-500 truncate">
            {isAllTime
              ? 'Total platform activity since go-live · 1 Mar 2026 → today'
              : `Activity volume across the portal · last ${range} days`}
          </p>
          {freshnessLabel && !isLoading && (
            <p
              className="text-[11px] text-gray-400 mt-0.5 truncate"
              title={
                generatedAt
                  ? `Figures computed ${generatedAt.toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })} SGT · refreshes when you return to this tab or tap Refresh`
                  : undefined
              }
            >
              {freshnessLabel}
              {!isFetching && ' · includes today through last sync'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="icon-button-admin disabled:opacity-50"
            aria-label="Refresh usage data"
            title="Refresh usage data"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
          <PillToggle
            value={range}
            onChange={setRange}
            options={RANGE_OPTIONS}
            variant="compact"
            theme="admin"
            ariaLabel="Usage analytics date range"
          />
        </div>
      </div>

      {isLoading ? (
        /* Full dashboard content load => skeleton (mirrors AdminDashboard reference) */
        <div className="space-y-6">
          {/* Headline + secondary metric card strips */}
          {[1, 2].map((strip) => (
            <div key={strip} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
                  <div className="flex items-center">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                    <div className="ml-2 sm:ml-4 space-y-2 min-w-0">
                      <div className="h-6 bg-gray-200 rounded w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Activity trend chart */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
            <div className="flex items-center justify-between mb-6">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
              <div className="h-8 bg-gray-200 rounded-full w-56 max-w-[50%]"></div>
            </div>
            <div className="h-[280px] bg-gray-100 rounded-lg"></div>
          </div>

          {/* Workflow funnels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-gray-200 rounded-full flex-shrink-0"></div>
                      <div className="space-y-2 min-w-0">
                        <div className="h-4 bg-gray-200 rounded w-12"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <div className="h-2 bg-gray-100 rounded-full w-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isError ? (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 text-center py-12">
          <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Usage analytics temporarily unavailable</p>
          <p className="text-sm text-gray-500 mt-2">Please try again in a moment</p>
        </div>
      ) : !hasActivity ? (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 text-center py-12">
          <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No portal activity recorded in this period</p>
          <p className="text-sm text-gray-500 mt-2">Try widening the date range</p>
        </div>
      ) : (
        <>
          {/* Headline metric cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <MetricCard
              icon={Clock}
              color="blue"
              value={formatNumber((totals.clock_in || 0) + (totals.clock_out || 0))}
              label="Clock Events"
              sub={`${formatNumber(totals.clock_in)} in · ${formatNumber(totals.clock_out)} out`}
            />
            <MetricCard
              icon={Calendar}
              color="amber"
              value={formatNumber(totals.leave_submitted)}
              label="Leave Requests"
              sub={`${leaveFunnel.approval_rate || 0}% approval rate`}
            />
            <MetricCard
              icon={FileText}
              color="red"
              value={formatNumber(totals.documents_uploaded)}
              label="Documents Uploaded"
              sub={`${formatNumber(totals.ocr_processed)} OCR runs`}
            />
            <MetricCard
              icon={Users}
              color="emerald"
              value={formatNumber(engagement.active_field_workers)}
              label={isAllTime ? 'Workers Reached' : 'Active Workers'}
              sub={
                isAllTime
                  ? `${engagement.adoption_rate || 0}% of ${formatNumber(engagement.total_employees)} ever clocked in`
                  : `${engagement.adoption_rate || 0}% of ${formatNumber(engagement.total_employees)}`
              }
            />
          </div>

          {/* Secondary metric strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <MetricCard
              icon={ShieldCheck}
              color="purple"
              value={formatNumber(totals.audit_events)}
              label="Audited Actions"
              sub={`${formatNumber(engagement.active_system_users)} active users`}
            />
            <MetricCard
              icon={LogIn}
              color="teal"
              value={formatNumber(totals.logins)}
              label="Logins"
            />
            <MetricCard
              icon={Download}
              color="indigo"
              value={formatNumber(totals.file_downloads)}
              label="Files Downloaded"
            />
            <MetricCard
              icon={Timer}
              color="gray"
              value={
                leaveFunnel.avg_approval_hours != null
                  ? `${leaveFunnel.avg_approval_hours}h`
                  : 'N/A'
              }
              label="Avg Leave Approval"
            />
          </div>

          {/* Activity trend chart */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="mb-4 sm:mb-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3 sm:justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 min-w-0 truncate">
                    {isAllTime ? 'Monthly Activity Trend' : 'Daily Activity Trend'}
                  </h3>
                </div>
                <PillToggle
                  value={chartView}
                  onChange={setChartView}
                  options={CHART_VIEW_OPTIONS}
                  variant="compact"
                  theme="admin"
                  className="flex-shrink-0 max-w-full"
                  ariaLabel="Activity trend series"
                />
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 ml-7 sm:ml-8">
                {isAllTime
                  ? `Since go-live: ${formatNumber(totals[activeSeries[0].key])} total ${activeSeries[0].name.toLowerCase()}`
                  : `Today: ${formatNumber(today[activeSeries[0].key])} ${activeSeries[0].name.toLowerCase()}`}
              </p>
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <defs>
                  {activeSeries.map((s) => (
                    <linearGradient key={s.key} id={`usage-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  stroke="#6b7280"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px',
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '12px' }} iconType="circle" />
                {activeSeries.map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.name}
                    stroke={s.color}
                    strokeWidth={2}
                    fill={`url(#usage-${s.key})`}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Workflow funnels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
            {/* Leave workflow */}
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Leave Workflow</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <FunnelStat icon={Calendar} color="amber" value={formatNumber(leaveFunnel.submitted)} label="Submitted" />
                <FunnelStat icon={CheckCircle} color="emerald" value={formatNumber(leaveFunnel.approved)} label="Approved" />
                <FunnelStat icon={XCircle} color="red" value={formatNumber(leaveFunnel.rejected)} label="Rejected" />
                <FunnelStat icon={Timer} color="gray" value={formatNumber(leaveFunnel.cancelled)} label="Cancelled" />
              </div>
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-600">Approval Rate</span>
                  <span className="text-xs font-semibold text-gray-900">{leaveFunnel.approval_rate || 0}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, leaveFunnel.approval_rate || 0)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Document & OCR pipeline */}
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <ScanLine className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Document & OCR Pipeline</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <FunnelStat icon={FileText} color="red" value={formatNumber(docFunnel.uploaded)} label="Uploaded" />
                <FunnelStat icon={ScanLine} color="purple" value={formatNumber(docFunnel.ocr_processed)} label="OCR Processed" />
                <FunnelStat icon={CheckCircle} color="emerald" value={formatNumber(docFunnel.approved)} label="Approved" />
                <FunnelStat icon={Timer} color="amber" value={formatNumber(docFunnel.pending_now)} label="Pending Now" />
              </div>
              <div className="pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-600">OCR Automation Rate</span>
                  <span className="text-xs font-semibold text-gray-900">{docFunnel.ocr_coverage || 0}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, docFunnel.ocr_coverage || 0)}%` }}
                  />
                </div>
                {docFunnel.avg_turnaround_hours != null && (
                  <p className="text-xs text-gray-500 mt-2">
                    Avg {formatHoursMinutes(docFunnel.avg_turnaround_hours)} from upload to approval
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Volume breakdowns by leave type and document type */}
          {(leaveTypeBreakdown.length > 0 || documentTypeBreakdown.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
              <BreakdownPanel
                icon={Calendar}
                iconClass="text-amber-600"
                title="Leave Types Submitted"
                totalLabel={`${formatNumber(totals.leave_submitted)} total`}
                items={leaveTypeBreakdown}
                barClassFor={(item) => leaveTypeBarClass(item.label)}
                maxCount={maxLeaveType}
              />
              <BreakdownPanel
                icon={FileText}
                iconClass="text-red-600"
                title="Document Types Uploaded"
                totalLabel={`${formatNumber(totals.documents_uploaded)} total`}
                items={documentTypeBreakdown}
                barClass="bg-red-500"
                maxCount={maxDocumentType}
              />
            </div>
          )}

          {/* Employee behaviour patterns — rolling weekday×hour window (self-fetches).
              In lifetime mode the section stays visible but uses RHYTHM_ROLLING_DAYS
              so deploy/maintenance timing stays meaningful while totals stay all-time. */}
          <PeakUsagePeriods
            range={isAllTime ? RHYTHM_ROLLING_DAYS : range}
            lifetimeContext={isAllTime}
          />
        </>
      )}
    </section>
  );
};

export default UsageAnalyticsSection;
